function padBase64(input: string): string {
  const mod = input.length % 4
  if (mod === 0) return input
  return input + '='.repeat(4 - mod)
}

export function base64UrlToUint8Array(input: string): Uint8Array {
  const normalized = padBase64(String(input || '').replace(/-/g, '+').replace(/_/g, '/'))
  const binary = atob(normalized)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export function bufferToBase64Url(input: ArrayBuffer | Uint8Array | null | undefined): string {
  if (!input) return ''
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

export function toCredentialCreationOptions(raw: any): CredentialCreationOptions {
  const publicKey = { ...(raw?.publicKey || raw?.public_key || {}) }
  publicKey.challenge = base64UrlToUint8Array(publicKey.challenge)
  if (publicKey.user?.id) {
    publicKey.user = { ...publicKey.user, id: base64UrlToUint8Array(publicKey.user.id) }
  }
  if (Array.isArray(publicKey.excludeCredentials)) {
    publicKey.excludeCredentials = publicKey.excludeCredentials.map((item: any) => ({
      ...item,
      id: base64UrlToUint8Array(item?.id),
    }))
  }
  return { publicKey }
}

export function toCredentialRequestOptions(raw: any): CredentialRequestOptions {
  const publicKey = { ...(raw?.publicKey || raw?.public_key || {}) }
  publicKey.challenge = base64UrlToUint8Array(publicKey.challenge)
  if (Array.isArray(publicKey.allowCredentials)) {
    publicKey.allowCredentials = publicKey.allowCredentials.map((item: any) => ({
      ...item,
      id: base64UrlToUint8Array(item?.id),
    }))
  }
  return { publicKey, mediation: raw?.mediation }
}

export function passkeyAuthenticationErrorMessage(error: unknown): string {
  const named = error as { name?: unknown; message?: unknown } | null
  if (String(named?.name || '') === 'NotAllowedError') {
    return 'The passkey request was not allowed. Complete sign-in in a top-level Safari or Chrome window, approve the passkey prompt, and try again if it was dismissed.'
  }
  return String(named?.message || error || 'Failed to verify passkey')
}

export function serializePasskeyRegistration(credential: Credential | null): any {
  const publicKeyCredential = credential as PublicKeyCredential | null
  const response = publicKeyCredential?.response as AuthenticatorAttestationResponse | null
  if (!publicKeyCredential || !response) throw new Error('Passkey registration was cancelled')
  return {
    id: publicKeyCredential.id,
    rawId: bufferToBase64Url(publicKeyCredential.rawId),
    type: publicKeyCredential.type,
    response: {
      attestationObject: bufferToBase64Url(response.attestationObject),
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      transports: typeof response.getTransports === 'function' ? response.getTransports() : undefined,
    },
    clientExtensionResults: publicKeyCredential.getClientExtensionResults?.() || {},
  }
}

export function serializePasskeyAuthentication(credential: Credential | null): any {
  const publicKeyCredential = credential as PublicKeyCredential | null
  const response = publicKeyCredential?.response as AuthenticatorAssertionResponse | null
  if (!publicKeyCredential || !response) throw new Error('Passkey authentication was cancelled')
  return {
    id: publicKeyCredential.id,
    rawId: bufferToBase64Url(publicKeyCredential.rawId),
    type: publicKeyCredential.type,
    response: {
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
    },
    clientExtensionResults: publicKeyCredential.getClientExtensionResults?.() || {},
  }
}
